<template>
  <vl-data-table-extended :fetching="loading" :data="items" :columns="columnMap" :meta="metaData" mod-zebra>
  </vl-data-table-extended>
</template>

<script lang="ts" setup>
import { computed, h, reactive, watch } from 'vue';
import { DeliveryQualityCheckDetailDto, GeckoTestDto, TestDto } from '@/api/backoffice/clients';
import { ColumnDefExtended } from '@/components/vl-data-table-extended/types';
import { RowData } from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import GeckoTestActiveToggle, { DeliveryQualityCheckData } from './GeckoTestActiveToggle.vue';
import GeckoTestActionButton from './GeckoTestActionButton.vue';

const { t } = useI18n();

const props = defineProps<{
  items: GeckoTestDto[];
  deliveryQualityCheckTypeId: number;
  isReadOnly?: boolean;
  loading?: boolean;
  onCheckboxChange?: (evt: DeliveryQualityCheckData) => Promise<boolean>;
  onRemoveClick?: (test: GeckoTestDto) => void;
  onEditClick?: (test: GeckoTestDto) => void;
  onInfoClick?: (test: GeckoTestDto) => void;
  canEditItem?: (test: GeckoTestDto) => boolean;
}>();

const emit = defineEmits(['update:qualityCheckState']);

const metaData = computed(() => {
  return {
    serverside: false,
    resultsPerPage: 15
  };
});

const data = reactive({
  deliveryQualityChecks: [] as DeliveryQualityCheckData[]
});

/*
  Populates the deliveryQualityChecks array based on the items prop to store reactive UI changes
*/
const populateQualityChecks = () => {
  data.deliveryQualityChecks = [];
  props.items?.forEach((item) => {
    const dataItem = {
      test: item.test,
      isEnabled: item.test.availability == 'enabled',
      deliveryQualityCheckTypeId: props.deliveryQualityCheckTypeId
    } as DeliveryQualityCheckData;
    data.deliveryQualityChecks.push(dataItem);
  });
};

watch(
  () => props.items,
  () => populateQualityChecks(),
  { immediate: true, deep: true }
);

const getDeliveryQualityCheckByCode = (code: string): DeliveryQualityCheckDetailDto => {
  const item = data.deliveryQualityChecks.find((dqc) => dqc.test.code === code);
  return item;
};

const createActiveToggleCell = (_props) => {
  // gets the original gecko test item from the row
  const test = (_props.row.original as GeckoTestDto).test;
  // gets the reactive data item to bind to the checkbox
  const deliveryQualityCheck = getDeliveryQualityCheckByCode(test.code);

  return h(GeckoTestActiveToggle, {
    modelValue: deliveryQualityCheck.isEnabled,
    deliveryQualityCheckTypeId: props.deliveryQualityCheckTypeId,
    useConfirmationDialog: props.deliveryQualityCheckTypeId == 0, // only use confirmation dialog for default checks
    test,
    onCheckboxChange: async (deliveryQualityCheckData: DeliveryQualityCheckData) => {
      return (await props.onCheckboxChange?.(deliveryQualityCheckData)) || true;
    },
    'onUpdate:modelValue': async (isEnabled: boolean) => {
      if (deliveryQualityCheck.isEnabled !== isEnabled) {
        deliveryQualityCheck.isEnabled = !isEnabled;
      }
    },
    modDisabled: props.isReadOnly
  });
};

const createEditActionButtonCell = (_props) => {
  return h(GeckoTestActionButton, {
    canEdit: props.canEditItem ? props.canEditItem(_props.row.original as GeckoTestDto) : false,
    isReadOnly: props.isReadOnly,
    onRemoveClick: async () => {
      const test = _props.row.original as GeckoTestDto;
      await props.onRemoveClick?.(test);
    },
    onEditClick: async () => {
      const test = _props.row.original as GeckoTestDto;
      await props.onEditClick?.(test);
    },
    onInfoClick: async () => {
      const test = _props.row.original as GeckoTestDto;
      await props.onInfoClick?.(test);
    }
  });
};

const columnMap = computed((): ColumnDefExtended<RowData>[] => {
  const columns: ColumnDefExtended<RowData>[] = [
    {
      accessorKey: 'test.id',
      header: t('geckodefaultteststable.list.table.header.id'),
      enableSorting: true,
      size: 1,
      link: true
    },
    {
      accessorKey: 'test.code',
      header: t('geckodefaultteststable.list.table.header.code'),
      enableSorting: true,
      size: 5,
      link: true
    },
    {
      accessorKey: 'test.name',
      header: t('geckodefaultteststable.list.table.header.name'),
      enableSorting: true,
      size: 60,
      link: true
    },
    {
      accessorKey: 'test.function',
      header: t('geckodefaultteststable.list.table.header.function'),
      enableSorting: true,
      size: 17,
      link: true,
      cell: (props) => {
        const functionValue = props.getValue() as string;
        if (!functionValue) {
          return '-';
        }
        return functionValue.charAt(0).toUpperCase() + functionValue.slice(1);
      }
    }
  ];

  const activeToggleColumn: ColumnDefExtended<RowData> = {
    accessorKey: 'placeholder',
    header: t('geckodefaultteststable.list.table.header.active'),
    enableSorting: true,
    size: 1,
    link: true,
    cell: (_props) => createActiveToggleCell(_props)
  };
  columns.push(activeToggleColumn);

  if (props.deliveryQualityCheckTypeId == 1) {
    const editActionButtonColumn: ColumnDefExtended<RowData> = {
      accessorKey: 'editAction',
      header: ' ',
      enableSorting: true,
      size: 10,
      link: true,
      cell: (_props) => createEditActionButtonCell(_props)
    };
    columns.push(editActionButtonColumn);
  }

  return columns;
});
</script>
